import { ControlledModal, Dialog, DialogContent } from "@/components"
import { DialogHeader } from "@/components/Dialog/DialogHeader"
import { Avatar, Spinner, Text } from "@/elements"
import { graphql } from "@/gql"
import { ConnectionsModalDocument } from "@/gql/graphql"
import { useHandleListEndReached } from "@/hooks/useHandleListEndReached"
import { useQuery } from "@apollo/client"
import { useMemo, useRef } from "react"
import Link from "next/link"

graphql(`
  query ConnectionsModal($userId: ID!, $first: Int!, $after: String) {
    user(id: $userId) {
      id
      mutualConnections: mutualFriends(first: $first, after: $after) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            __typename
            id
            name
            avatarInitials
            avatar120w: avatar(width: 120)
            professionString
            ... on IndividualCreative {
              handle
            }
          }
        }
      }
    }
  }
`)

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  showMutuals?: boolean
  userId: string
}

const PAGE_SIZE = 15

export const ConnectionsModal: React.FC<Props> = ({ open, onOpenChange, userId, showMutuals = true }) => {
  const { data, fetchMore, networkStatus, loading } = useQuery(ConnectionsModalDocument, {
    variables: {
      userId: userId,
      first: PAGE_SIZE,
    },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    context: { version: 2 },
  })
  const loadMoreRef = useRef(null)

  const connections = data?.user?.mutualConnections
  const totalCount = connections?.totalCount

  useHandleListEndReached({
    pageInfo: connections?.pageInfo,
    first: PAGE_SIZE,
    fetchMore,
    networkStatus,
    observerRef: loadMoreRef,
  })

  const title = useMemo(() => {
    if (showMutuals) {
      return !loading ? `Your mutual connections (${connections?.totalCount || 0})` : "Your mutual connections"
    }

    return !loading ? `Your connections (${connections?.totalCount || 0})` : "Your connections"
  }, [showMutuals, totalCount, loading])

  return (
    <ControlledModal
      open={open}
      onOpenChange={onOpenChange}
      header={{ title: title }}
      body={
        <>
          <div className="mt-3 flex flex-col justify-center gap-2 overflow-y-scroll">
            {connections?.edges?.map(edge => {
              const node = edge?.node
              if (!node) return
              return (
                <Link href={`/${node.handle}`} key={node?.id}>
                  <div className="flex flex-row items-center rounded-lg px-3 py-2 hover:bg-bg-2">
                    <Avatar size="md" src={node?.avatar120w} initials={node?.avatarInitials} />
                    <div className="ml-3">
                      <Text>{node.name}</Text>
                      <Text className="text-fg-2" variant="label">
                        {node.professionString}
                      </Text>
                    </div>
                  </div>
                </Link>
              )
            })}
            <div className="relative mt-2 flex h-[20px] w-full flex-row items-center justify-center" ref={loadMoreRef}>
              {connections?.pageInfo?.hasNextPage && <Spinner size="medium" />}
            </div>
          </div>
        </>
      }
    />
  )
}
